import LayersClearIcon from '@mui/icons-material/LayersClear'
import LoginIcon from '@mui/icons-material/Login'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import ReplayIcon from '@mui/icons-material/Replay'
import { IconButton, Tooltip, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useContext } from 'react'
import { createPortal } from 'react-dom'
import {
  askCheckedInChamberScreenToPollNextAction,
  sendReloadChamberRoomScreenSignal,
  sendShowImageCommandToChamberScreen,
} from 'src/consumer/services/featureServices'
import { ChamberScreenSignallingSnackBarContext } from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/ChamberScreenSignallingSnackBarProvider'

const ActionsContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  left: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 1400,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '8px 6px', // Reduced padding: vertical 8px, horizontal 6px for narrower width
  background: 'transparent', // Fully transparent background
  backdropFilter: 'none', // Remove backdrop filter for transparency
  WebkitBackdropFilter: 'none',
  borderRadius: '12px',
  border: 'none', // Remove border for cleaner look
  boxShadow: 'none', // Remove shadow for transparency
  width: 'fit-content', // Fit content width
  minWidth: 'auto', // No minimum width constraint
  maxWidth: 'fit-content', // Maximum width fits content
  // Responsive sizing based on screen width
  [theme.breakpoints.down('lg')]: {
    left: '12px',
    gap: '6px',
    padding: '6px 4px', // Even tighter on smaller screens
  },
  [theme.breakpoints.down('md')]: {
    display: 'none', // Hide completely on mobile
  },
  [theme.breakpoints.up('xl')]: {
    left: '20px',
    gap: '10px',
    padding: '10px 8px', // Slightly more padding on very large screens
  },
}))

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: '48px',
  height: '48px',
  minWidth: '48px', // Prevent shrinking
  minHeight: '48px',
  maxWidth: '48px', // Prevent growing
  maxHeight: '48px',
  background: 'linear-gradient(135deg, rgba(103, 121, 227, 0.1) 0%, rgba(116, 80, 170, 0.1) 100%)',
  border: '1.5px solid rgba(103, 121, 227, 0.2)',
  color: '#6779e3',
  transition: 'all 0.2s ease',
  flexShrink: 0, // Prevent flex shrinking
  flexGrow: 0, // Prevent flex growing
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(103, 121, 227, 0.2) 0%, rgba(116, 80, 170, 0.2) 100%)',
    borderColor: 'rgba(103, 121, 227, 0.4)',
    transform: 'translateY(-2px) scale(1.05)',
    boxShadow: '0 4px 12px rgba(103, 121, 227, 0.3)',
  },
  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  },
  // Responsive button sizes
  [theme.breakpoints.down('lg')]: {
    width: '44px',
    height: '44px',
    minWidth: '44px',
    minHeight: '44px',
    maxWidth: '44px',
    maxHeight: '44px',
  },
  [theme.breakpoints.down('md')]: {
    width: '40px',
    height: '40px',
    minWidth: '40px',
    minHeight: '40px',
    maxWidth: '40px',
    maxHeight: '40px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '52px',
    height: '52px',
    minWidth: '52px',
    minHeight: '52px',
    maxWidth: '52px',
    maxHeight: '52px',
  },
}))

export default function ChamberScreenSignallingActions({ chamberRoomId }) {
  const { showSnackBar } = useContext(ChamberScreenSignallingSnackBarContext)

  return (
    <>
      {createPortal(
        <ActionsContainer className="hide-on-print-only">
          <Tooltip title="Show Payment QR on screen - 30s" placement="right" arrow>
            <StyledIconButton
              onClick={async () => {
                await sendShowImageCommandToChamberScreen(chamberRoomId, 'payment-qr')
                showSnackBar('Showing Payment QR on Room Screen.', 30, chamberRoomId)
              }}
            >
              <QrCodeScannerIcon sx={{ 
                fontSize: { xs: 20, sm: 20, md: 22, lg: 22, xl: 24 } 
              }} />
            </StyledIconButton>
          </Tooltip>
          <Tooltip title="Fast-track screen connect (BETA)" placement="right" arrow>
            <StyledIconButton
              onClick={() => {
                askCheckedInChamberScreenToPollNextAction(chamberRoomId)
              }}
            >
              <LoginIcon sx={{ 
                fontSize: { xs: 20, sm: 20, md: 22, lg: 22, xl: 24 } 
              }} />
            </StyledIconButton>
          </Tooltip>
          <Tooltip title="Clear Room Screen" placement="right" arrow>
            <StyledIconButton
              onClick={async () => {
                sendShowImageCommandToChamberScreen(chamberRoomId, 'clear-screen')
              }}
            >
              <LayersClearIcon sx={{ 
                fontSize: { xs: 20, sm: 20, md: 22, lg: 22, xl: 24 } 
              }} />
            </StyledIconButton>
          </Tooltip>
          <Tooltip title="Refresh/Reload Room Screen" placement="right" arrow>
            <StyledIconButton
              onClick={async () => {
                await sendReloadChamberRoomScreenSignal(chamberRoomId)
                showSnackBar('Reloading Room Screen in', 3, chamberRoomId, <></>)
              }}
            >
              <ReplayIcon sx={{ 
                fontSize: { xs: 20, sm: 20, md: 22, lg: 22, xl: 24 } 
              }} />
            </StyledIconButton>
          </Tooltip>
        </ActionsContainer>,
        document.body,
      )}
    </>
  )
}

{
  /* <div style={{ position: 'absolute', left: '16px', top: '16px', color: '#fff' }}>
        <IconButton id="demo-positioned-button-counter" onClick={handleClickMenuOpener}>
          <MoreVertIcon style={{ color: '#fff' }} />
        </IconButton>
        <Menu
          id="demo-positioned-menu"
          aria-labelledby="demo-positioned-button"
          anchorEl={anchorElForMenu}
          open={menuOpen}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
                  onClick={() => {
                    sendShowImageCommandToChamberScreen(
                      serverGivenNextAction?.channel?.chamberRoomId,
                      'therapy-support-whatsapp-qr',
                    )
                    handleCloseMenu()
                  }}
                >
                  Show Therapy Support QR on screen - 30s
                </MenuItem>
          <MenuItem
            onClick={() => {
              sendShowImageCommandToChamberScreen(chamberRoomId, 'payment-qr')
              handleCloseMenu()
            }}
          >
            Show Payment QR on screen - 30s
          </MenuItem>
          <MenuItem
            onClick={() => {
              askCheckedInChamberScreenToPollNextAction(chamberRoomId)
              handleCloseMenu()
            }}
          >
            Fast-track screen connect (BETA)
          </MenuItem>
          <MenuItem
            onClick={() => {
              sendShowImageCommandToChamberScreen(chamberRoomId, 'clear-screen')
              handleCloseMenu()
            }}
          >
            Clear Room Screen
          </MenuItem>
          <MenuItem
            onClick={() => {
              sendReloadChamberRoomScreenSignal(chamberRoomId)
              handleCloseMenu()
            }}
          >
            Refresh/Reload Room Screen
          </MenuItem>
        </Menu>
      </div> */
}
